/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import {
  Grid,
  Box,
  IconButton,
  Chip,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { dataGridCustomizations } from "../../../../../theme/customizations";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import AppTheme from "../../../../../theme/AppTheme";
import {
  getInvoiceList,
  statusUpdate,
} from "../../../../../service/Invoice/invoice";
import { useNavigate, useParams } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PaginationButtons from "../../../../../components/Pagination/PaginationButtons";
import {
  notifyError,
  notifySuccess,
} from "../../../../../components/ToastContainer/Toast/Toast";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import ShipmentForm from "./ShipmentForm/ShipmentForm";
import RtoForm from "./RtoForm/RtoForm";
import AddIcon from "@mui/icons-material/Add";
import HideSourceIcon from "@mui/icons-material/HideSource";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { packingLabel } from "../../../../../service/order/orderService";
import ViewInvoicePopup from "../ViewInvoice/ViewInvoicePopup";
import GenericFilter from "../../../../../components/GenericFilter/GenericFilter";

const xThemeComponents = {
  ...dataGridCustomizations,
};

interface InvoiceItem {
  id: number;
  order_id: number;
  code: string;
  status: string;
  invoice_date: string;
  total_amount: number;
  payment_method: string;
  customer: {
    name: string;
    email: string;
    phone: string;
  };
}

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

const invoiceStatusFlow = [
  "billed",
  "packed",
  "agent-assigned",
  "picked",
  "out-for-delivery",
  "rto-initiated",
  "rto-disposed",
  "rto-delivered",
  "delivered",
];

export default function InvoiceGrid(props: { disableCustomTheme?: boolean }) {
  const [rows, setRows] = useState<InvoiceItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(1);
  const [limit, setLimit] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<any>(null);
  const [flag, setFlag] = useState(false);
  const navigate = useNavigate();
  const { store_id } = useParams();
  const [open, setOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [rtoOpen, setRtoOpen] = useState(false);
  const [dialogType, setDialogType] = useState<"shipment" | "rto" | null>(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [openViewInvoice, setOpenViewInvoice] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [ordersId, setOrdersId] = useState(null);

  const rtoCancelReason = localStorage.getItem("rtoCancelReason");

  const filterInvoiceStatusOptions = (currentStatus: string) => {
    const index = invoiceStatusFlow.indexOf(currentStatus.toLowerCase());
    if (index === -1) return [];

    let nextStatuses = invoiceStatusFlow.slice(index + 1);

    const rtoStatuses = ["rto-initiated", "rto-disposed", "rto-delivered"];
    const isRtoStatus = rtoStatuses.includes(currentStatus.toLowerCase());

    if (isRtoStatus) {
      nextStatuses = nextStatuses.filter((status) => status !== "delivered");
    }

    if (
      ["rto-initiated", "rto-disposed"].includes(currentStatus.toLowerCase())
    ) {
      return nextStatuses.filter((status) =>
        ["rto-disposed", "rto-delivered"].includes(status)
      );
    }

    if (currentStatus.toLowerCase() === "rto-delivered") {
      return [];
    }

    if (
      [
        "billed",
        "packed",
        "agent-assigned",
        "picked",
        "out-for-delivery",
      ].includes(currentStatus.toLowerCase())
    ) {
      return nextStatuses.filter((status) => !status.startsWith("rto-"));
    }

    if (!rtoCancelReason) {
      return nextStatuses.filter((status) => !status.startsWith("rto-"));
    }

    return nextStatuses;
  };

  let lastUpdatedId = null;

  const handleStatusChange = async (
    event: SelectChangeEvent,
    row: InvoiceItem
  ) => {
    const newStatus = event.target.value;

    if (lastUpdatedId === row.id) return;
    lastUpdatedId = row.id;

    setRows((prev) =>
      prev.map((item) =>
        item.id === row.id ? { ...item, status: newStatus } : item
      )
    );

    const statusPayload = { status: newStatus };
    const res = await statusUpdate(row.id, statusPayload);
    if (res.status === 200) {
      notifySuccess("Status Updated");
    }

    // setTimeout(() => {
    //     lastUpdatedId = null;
    // }, 1000);
  };

  const convertToLocalDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    });
  };

  const handlePackedLabel = async (invoiceId) => {
    try {
      const response = await packingLabel(invoiceId, {
        responseType: "arraybuffer",
      });
      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `ShipmentLabel_${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      notifyError("Something Went Wrong");
    }
  };

  const handleFetchInvoiceList = async (filtersData?: any, isFromFilter?: boolean) => {
    try {
      // If filtersData is provided (from GenericFilter), process it directly
      if (isFromFilter && filtersData !== undefined && filtersData !== null) {
        setFilters(filtersData || {});
        setFlag(true);

        // Safeguard: support both .data and .invoice property names, fallback to empty array
        const invoices = filtersData.data || filtersData.invoice || [];

        const formattedRows = invoices.map((invoice: any) => ({
          id: invoice.id,
          invoice_date: convertToLocalDate(invoice.invoice_date),
          order_id: invoice.order_id,
          customer_name: invoice.customer?.name || "N/A",
          payment_method: invoice.payment_method,
          status: invoice.status,
          total_amount: Number(invoice.total_amount).toFixed(2),
        }));

        setRows(formattedRows);
        if (typeof filtersData?.total === "number") setTotal(filtersData.total);
        if (typeof filtersData?.totalPages === "number") setTotalPages(filtersData.totalPages);
        return;
      }

      // Normal fetch case (used for initial load, page changes, and resets)
      setFlag(false);
      const response = await getInvoiceList(currentPage, store_id);
      const dataContainer = response?.data?.data;
      const invoices = dataContainer?.invoice || dataContainer?.data || [];

      const formattedRows = invoices.map((invoice: any) => ({
        id: invoice.id,
        invoice_date: convertToLocalDate(invoice.invoice_date),
        order_id: invoice.order_id,
        customer_name: invoice.customer?.name || "N/A",
        payment_method: invoice.payment_method,
        status: invoice.status,
        total_amount: Number(invoice.total_amount).toFixed(2),
      }));

      setRows(formattedRows);
      if (dataContainer) {
        setLimit(dataContainer.limit || 10);
        setTotal(dataContainer.total || 0);
        setTotalPages(dataContainer.totalPages || 1);
      }
    } catch (error) {
      console.error("Fetch Invoice Error:", error);
      notifyError("Something Went Wrong");
    }
  };

  useEffect(() => {
    // Only fetch data if not using filter-based pagination
    // Filter-based pagination is handled by GenericFilter component
    if (!flag) {
      handleFetchInvoiceList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, open]);

  const columns: GridColDef[] = [
    { field: "invoice_date", headerName: "Date", minWidth: 130, flex: 1 },
    { field: "order_id", headerName: "Order No", minWidth: 120, flex: 1 },
    { field: "id", headerName: "Invoice No", minWidth: 120, flex: 1 },
    {
      field: "customer_name",
      headerName: "Customer Name",
      minWidth: 180,
      flex: 1,
    },
    {
      field: "payment_method",
      headerName: "Payment Method",
      minWidth: 160,
      flex: 1,
    },
    { field: "total_amount", headerName: "Amount (₹)", minWidth: 100, flex: 1 },
    {
      field: "status",
      headerName: "Status",
      headerAlign: "center",
      minWidth: 210,
      flex: 1,
      renderCell: (params) => {
        let currentStatus = params.row.status.toLowerCase();
        const invId = String(params.row.id);
        const logistics = JSON.parse(
          localStorage.getItem("shipmentStatus") || "{}"
        );
        const matchedLogistics = Object.entries(logistics).find(
          ([key, value]) => key === invId
        );
        const rtoCancel = JSON.parse(
          localStorage.getItem("rtoCancelReason") || "[]"
        );
        const matched = rtoCancel.find((item) => item[invId] !== undefined);

        if (matchedLogistics && currentStatus === "billed") {
          const logisticsEvent = {
            target: { value: "packed" },
          } as SelectChangeEvent;

          handleStatusChange(logisticsEvent, params.row);
          currentStatus = "packed";
        }

        if (matched && currentStatus === "out-for-delivery") {
          const rtoEvent = {
            target: { value: "rto-initiated" },
          } as SelectChangeEvent;

          handleStatusChange(rtoEvent, params.row);
          currentStatus = "rto-initiated";
        }

        if (currentStatus === "packed") {
          setTimeout(() => {
            localStorage.removeItem("shipmentStatus");
          }, 2000);
        }
        if (currentStatus === "rto-initiated") {
          setTimeout(() => {
            localStorage.removeItem("rtoCancelReason");
          }, 3000);
        }

        if (currentStatus.startsWith("rto-")) {
          currentStatus = currentStatus.replace("rto-", "RTO-");
        }
        const options = filterInvoiceStatusOptions(currentStatus);
        const isFinalStatus =
          currentStatus === "delivered" || currentStatus === "cancelled";
        const noDropdownNeeded = options.length === 0 || isFinalStatus;

        const statusLabel =
          currentStatus.charAt(0).toUpperCase() +
          currentStatus.slice(1).replace(/-/g, " ");

        const getStatusStyles = (status: string) => {
          // const isDisabled = status === "billed";

          // if (isDisabled) {
          //     return {
          //         backgroundColor: "#f5f5f5",
          //         color: "#9e9e9e",
          //         border: "1px solid #bdbdbd"
          //     };
          // }
          switch (status.toLowerCase()) {
            case "cancelled":
            case "rto-disposed":
              return {
                backgroundColor: "rgba(243, 153, 148, 0.30)",
                color: "#b71c1c !important",
                border: "1px solid rgb(235, 43, 43, 0.60)",
              };
            case "delivered":
            case "rto-delivered":
              return {
                backgroundColor: "#f0fff4",
                color: "#087f23",
                border: "1px solid #6ee7b7",
              };
            default:
              return {
                backgroundColor: "rgba(120, 198, 250, 0.30)",
                color: "#fff",
                border: "1px solid rgb(0, 135, 225)",
              };
          }
        };

        const statusStyles = getStatusStyles(currentStatus);

        if (noDropdownNeeded) {
          return (
            <Chip
              label={statusLabel}
              variant="outlined"
              sx={{
                textTransform: "capitalize",
                fontWeight: 600,
                width: "100%",
                p: 2,
                borderRadius: "2rem",
                maxWidth: "10rem",
                ...statusStyles,
              }}
            />
          );
        }

        return (
          <>
            {currentStatus === "billed" ? (
              <LightTooltip
                title="To click action button to create shipment and enable status"
                placement="top"
              >
                <Box sx={{ width: "100%" }}>
                  <Select
                    value={currentStatus}
                    onChange={(e) => handleStatusChange(e, params.row)}
                    IconComponent={ArrowDropDownIcon}
                    // disabled
                    variant="standard"
                    sx={{
                      width: "100%",
                      minWidth: "10rem",
                      backgroundColor: "transparent",
                      border: "none",
                      boxShadow: "none",
                    }}
                    renderValue={(selected) => {
                      const label =
                        selected.charAt(0).toUpperCase() +
                        selected.slice(1).replace(/-/g, " ");
                      const style = getStatusStyles(selected);
                      return (
                        <Chip
                          label={label}
                          variant="outlined"
                          sx={{
                            textTransform: "capitalize",
                            fontWeight: 600,
                            width: "100%",
                            maxWidth: "10rem",
                            p: 2,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            borderRadius: "2rem",
                            ...style,
                          }}
                        />
                      );
                    }}
                  >
                    {options.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option.charAt(0).toUpperCase() +
                          option.slice(1).replace(/-/g, " ")}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </LightTooltip>
            ) : (
              <Select
                value={currentStatus}
                onChange={(e) => handleStatusChange(e, params.row)}
                IconComponent={ArrowDropDownIcon}
                variant="standard"
                sx={{
                  width: "100%",
                  minWidth: "10rem",
                  backgroundColor: "transparent",
                  border: "none",
                  boxShadow: "none",
                }}
                renderValue={(selected) => {
                  // const label = selected.charAt(0).toUpperCase() + selected.slice(1).replace(/-/g, " ") + selected.startsWith('rto-').replace('rto-','RTO-');
                  const label = selected.startsWith("rto-")
                    ? "RTO-" + selected.slice(4).replace(/-/g, " ")
                    : selected.charAt(0).toUpperCase() +
                    selected.slice(1).replace(/-/g, " ");
                  const style = getStatusStyles(selected);
                  return (
                    <Chip
                      label={label}
                      variant="outlined"
                      sx={{
                        textTransform: "capitalize",
                        fontWeight: 600,
                        width: "100%",
                        maxWidth: "10rem",
                        p: 2,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        borderRadius: "2rem",
                        ...style,
                      }}
                    />
                  );
                }}
              >
                {options.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option.charAt(0).toUpperCase() +
                      option.slice(1).replace(/-/g, " ")}
                  </MenuItem>
                ))}
              </Select>
            )}
          </>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 70,
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const currentStatus = params.row.status.toLowerCase();

        return (
          <>
            {currentStatus === "billed" ? (
              <LightTooltip title="Create Shipment" placement="right">
                <IconButton onClick={() => handleClickOpen(params.row)}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </LightTooltip>
            ) : currentStatus === "packed" ? (
              <LightTooltip title="Packing Label" placement="right">
                <IconButton
                  onClick={() => {
                    console.log("params", params.row.id);
                    handlePackedLabel(params.row.id);
                  }}
                >
                  <ReceiptLongOutlinedIcon fontSize="small" />
                </IconButton>
              </LightTooltip>
            ) : (
              <LightTooltip title="No Action" placement="right">
                <IconButton disabled>
                  <HideSourceIcon fontSize="small" />
                </IconButton>
              </LightTooltip>
            )}
          </>
        );
      },
    },
    {
      field: "view",
      headerName: "View",
      minWidth: 70,
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return (
          <LightTooltip title="View" placement="right">
            <IconButton onClick={() => navigate(`/stores/${store_id}/invoice/${params.row.id}`)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </LightTooltip>
        );
      },
    },
  ];

  const handleChangePage = (page: number) => {
    setCurrentPage(page);
  };

  const resetCallback = () => {
    setFilters(null);
    setFlag(false);
    setRows([]);
  };

  const customPagination = () => {
    if (!rows || rows.length === 0 || !total || !limit || !totalPages) {
      return null;
    }
    <PaginationButtons
      count={totalPages}
      page={currentPage}
      totalItems={total}
      itemsPerPage={limit}
      onChange={handleChangePage}
    />
  };

  const handleClickOpen = (row) => {
    setOpen(true);
    setDialogType("shipment");
    setSelectedRowId(row.id);
  };

  const handleClose = () => {
    setOpen(false);
    setRtoOpen(false);
    setDialogType(null);
    setSelectedRowId(null);
  };

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <Box sx={{ mb: 2 }}>
        <GenericFilter
          entity="invoice"
          handleGetFetchData={handleFetchInvoiceList}
          store_id={store_id}
          page={currentPage}
          limit={limit}
          resetCallBack={resetCallback}
        />
      </Box>
      <CssBaseline enableColorScheme />
      <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" } }}>
        <Grid container>
          <Grid item xs={12} sx={{ height: 520 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              disableColumnMenu
              paginationMode="server"
              slots={{ pagination: customPagination }}
              localeText={{
                noRowsLabel: 'No Invoice',
              }}
            />
          </Grid>
        </Grid>
      </Box>

      <Dialog
        open={dialogType !== null}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="lg"
        fullWidth
      >
        {dialogType === "shipment" && (
          <ShipmentForm handleClose={handleClose} invoiceId={selectedRowId} />
        )}
        {dialogType === "rto" && (
          <RtoForm handleClose={handleClose} invoiceId={selectedRowId} />
        )}
      </Dialog>

      {openViewInvoice && (
        <ViewInvoicePopup open={openViewInvoice} close={() => setOpenViewInvoice(false)} invoiceId={ordersId} />
      )}

    </AppTheme>
  );
}
